import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewTableComponent from '../../../components/NewTableComponent';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { vehicleTypeStatusOptions, vehicleClassOptions } from '../../../utils/Dropdowns';
import { Badge } from 'react-bootstrap';
import VehicleTypeOverviewCards from './VehicleTypeOverviewCards';
import CreateVehicleTypeModal from './CreateVehicleTypeModal';
import UpdateVehicleTypeModal from './UpdateVehicleTypeModal';
import { plusIcon } from '../../../utils/svgs';
import Button from '../../../components/button';
import { LuPencil } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import CustomModal from '../../../components/CustomModal';
import { deleteVehicleTypeAsync, fetchVehicleTypes, getVehicleTypesOverview } from '../../../store/Slices/VehicleTypeSlice';
import { errorNotify, successNotify } from '../../../utils/toast';

const VehicleType = () => {
    const dispatch = useDispatch();
    const { vehicleTypesData, vehicleTypesLoading, deleteVehicleTypeLoading } = useSelector((state) => state.vehicleTypes);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [searchFilters, setSearchFilters] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedVehicleType, setSelectedVehicleType] = useState(null);
    const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState(null);

    const handleEditVehicleType = (vehicleType) => {
        setSelectedVehicleType(vehicleType);
        setShowUpdateModal(true);
    };

    const handleDeleteVehicleType = async () => {
        try {
            await dispatch(deleteVehicleTypeAsync(selectedVehicleTypeId)).unwrap();
            successNotify('Vehicle type deleted successfully');
            setShowDeleteModal(false);
            setSelectedVehicleTypeId(null);
            
            // Refresh table and overview
            dispatch(fetchVehicleTypes({
                page: currentPage,
                limit: limit,
                body: {}
            }));
            dispatch(getVehicleTypesOverview());
        } catch (error) {
            errorNotify(error);
        }
    };

    useEffect(() => {
        const transformedFilters = {};
        Object.keys(searchFilters).forEach(key => {
            let value = searchFilters[key].value;
            let filterType = searchFilters[key].filter_type || 'contains';

            if (Array.isArray(value)) {
                filterType = 'one_of';
            } else if (filterType === 'one_of') {
                value = [value];
            }

            if ((key === 'statusFilter' || key === 'vehicleClassFilter') && !Array.isArray(value)) {
                filterType = 'equals';
            }

            transformedFilters[key] = {
                value: value,
                filter_type: filterType
            };
        });

        dispatch(fetchVehicleTypes({
            page: currentPage,
            limit: limit,
            body: {
                ...transformedFilters,
                search_text: searchText
            }
        }));
    }, [currentPage, limit, searchText, searchFilters, dispatch]);

    const columns = [
        {
            name: "ID",
            key: "id",
            selector: (row) => row?.id,
            width: "80px",
            tableColumn: true,
            filterName: "idFilter",
            type: "number",
        },
        {
            name: "Title",
            key: "title",
            selector: (row) => row?.title,
            tableColumn: true,
            filterName: "titleFilter",
            type: "text",
        },
        {
            name: "Vehicle Class",
            key: "vehicle_class",
            selector: (row) => (
                <Badge bg="secondary" className="text-capitalize">
                    {row?.vehicle_class}
                </Badge>
            ),
            tableColumn: true,
            filterName: "vehicleClassFilter",
            type: "text",
            options: vehicleClassOptions
        },
        {
            name: "Status",
            key: "status",
            selector: (row) => (
                <Badge bg={row?.status === 'active' ? 'success' : 'danger'} className="text-capitalize">
                    {row?.status === 'active' ? 'True' : 'False'}
                </Badge>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            options: vehicleTypeStatusOptions
        },
        {
            name: "Created At",
            key: "created_at",
            selector: (row) => row?.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
            width: "150px",
        },
        {
            name: "Actions",
            key: "actions",
            width: "100px",
            selector: (row) => (
                <div className="table_action_btn">
                    <button onClick={() => handleEditVehicleType(row)} className="edit_btn">
                        <LuPencil />
                    </button>
                    <button onClick={() => {
                        setSelectedVehicleTypeId(row?.id);
                        setShowDeleteModal(true);
                    }} className="delete_btn">
                        <RiDeleteBinLine />
                    </button>
                </div>
            ),
        }
    ];

    return (
        <div className="container-fluid py-4">
            <Breadcrumbs values={['Maintenance', 'Vehicle Types']} />

            <div className='page_header access_view mb-4'>
                <h2>Vehicle Types</h2>
                <div className='d-flex align-items-center gap-2'>
                    <Button
                        submitText={<span>{plusIcon} Add Vehicle Type</span>}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={true}
                        submitBtn={() => setShowCreateModal(true)}
                        isShowCancel={false}
                    />
                </div>
            </div>
            
            <VehicleTypeOverviewCards />

            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <NewTableComponent
                        pageName="Vehicle Types"
                        columns={columns}
                        data={vehicleTypesData?.results || []}
                        totalItems={vehicleTypesData?.meta?.totalItems || 0}
                        totalPages={vehicleTypesData?.meta?.totalPages || 0}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        loading={vehicleTypesLoading}
                        onSearch={(text) => {
                            setSearchText(text);
                            setCurrentPage(1);
                        }}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                    />
                </div>
            </div>

            <CreateVehicleTypeModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                currentPage={currentPage}
                limit={limit}
            />

            <UpdateVehicleTypeModal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedVehicleType(null);
                }}
                vehicleType={selectedVehicleType}
                currentPage={currentPage}
                limit={limit}
            />

            <CustomModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} size="md" heading="Delete Vehicle Type" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this vehicle type?</p>

                    <Button
                        cancelBtn={() => setShowDeleteModal(false)}
                        submitBtn={handleDeleteVehicleType}
                        loading={deleteVehicleTypeLoading}
                        disabled={deleteVehicleTypeLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                        isShowCancel={true}
                    />
                </div>
            </CustomModal>
        </div>
    );
};

export default VehicleType;
